import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Star, Trash2, Edit, X, Clock, Check, Share2, Download } from 'lucide-react';
import api from '../utils/api';
import { SPORTS_CONFIG, getPrimaryStatLabel, getStatsByCategory, hasBattingPitching } from '../config/sports';
import { format } from 'date-fns';

export default function GameDetail() {
  const { athleteId, gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [editStats, setEditStats] = useState({});
  const [editNotes, setEditNotes] = useState('');
  const [canEdit, setCanEdit] = useState({ can_edit: false, hours_remaining: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [editMode, setEditMode] = useState('batting');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const shareCardRef = useRef(null);

  useEffect(() => {
    fetchGame();
  }, [gameId]);

  const fetchGame = async () => {
    try {
      const [gameRes, editRes, athleteRes] = await Promise.all([
        api.get(`/games/${gameId}`),
        api.get(`/games/${gameId}/can-edit`),
        api.get(`/athletes/${athleteId}`)
      ]);
      setGame(gameRes.data);
      setCanEdit(editRes.data);
      setEditStats(gameRes.data.stats || {});
      setEditNotes(gameRes.data.notes || '');
      setAthlete(athleteRes.data);
    } catch (error) {
      toast.error('Failed to load game');
      navigate(`/athlete/${athleteId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStar = async () => {
    try {
      const response = await api.put(`/games/${gameId}`, {
        is_starred: !game.is_starred
      });
      setGame(response.data);
      toast.success(response.data.is_starred ? 'Game starred!' : 'Star removed');
    } catch (error) {
      toast.error('Failed to update game');
    }
  };

  const handleDeleteGame = async () => {
    try {
      await api.delete(`/games/${gameId}`);
      toast.success('Game deleted');
      navigate(`/athlete/${athleteId}`);
    } catch (error) {
      toast.error('Failed to delete game');
    }
  };

  const handleStatChange = (key, delta) => {
    setEditStats(prev => ({
      ...prev,
      [key]: Math.max(0, (prev[key] || 0) + delta)
    }));
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      const response = await api.put(`/games/${gameId}`, {
        stats: editStats,
        notes: editNotes
      });
      setGame(response.data);
      setShowEditModal(false);
      toast.success('Game updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update game');
    } finally {
      setIsSaving(false);
    }
  };

  const generateShareImage = async () => {
    setIsGeneratingImage(true);
    try {
      // Use html2canvas to capture the share card
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: '#000000',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      return canvas;
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate image');
      return null;
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleShare = async () => {
    const canvas = await generateShareImage();
    if (!canvas) return;

    canvas.toBlob(async (blob) => {
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], `${athlete?.name || 'Game'}_${format(new Date(game.date_played), 'MMM_d_yyyy')}.png`, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: `${athlete?.name}'s Game Stats`,
              text: `Check out ${athlete?.name}'s ${sportConfig?.name} game - ${game.points} ${primaryLabel}!`
            });
            setShowShareModal(false);
            return;
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Share failed:', error);
          }
        }
      }
      // Fallback: download the image
      handleDownload();
    }, 'image/png');
  };

  const handleDownload = async () => {
    const canvas = await generateShareImage();
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `${athlete?.name || 'Game'}_${format(new Date(game.date_played), 'MMM_d_yyyy')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Image saved! You can now share it via text.');
    setShowShareModal(false);
  };

  if (isLoading || !game) {
    return (
      <div className="min-h-screen bg-[#000] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00E5E5] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const sportConfig = SPORTS_CONFIG[game.sport];
  const primaryLabel = getPrimaryStatLabel(game.sport);
  const showBattingPitching = hasBattingPitching(game.sport);

  // Get stats based on mode for baseball/softball
  const getEditableStats = () => {
    if (showBattingPitching) {
      return getStatsByCategory(game.sport, editMode);
    }
    return sportConfig?.stats || [];
  };

  return (
    <div className="min-h-screen bg-[#000] pb-24">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <button
          data-testid="back-btn"
          onClick={() => navigate(`/athlete/${athleteId}`)}
          className="flex items-center gap-1 text-white"
        >
          <ChevronLeft className="w-6 h-6" />
          <span>Back</span>
        </button>
        <div className="flex gap-2">
          <button
            data-testid="share-game-btn"
            onClick={() => setShowShareModal(true)}
            className="p-2 text-gray-400 hover:text-[#00E5E5]"
            title="Share game"
          >
            <Share2 className="w-6 h-6" />
          </button>
          {canEdit.can_edit && (
            <button
              data-testid="edit-game-btn"
              onClick={() => setShowEditModal(true)}
              className="p-2 text-gray-400 hover:text-[#00E5E5]"
              title="Edit game"
            >
              <Edit className="w-6 h-6" />
            </button>
          )}
          <button
            data-testid="star-btn"
            onClick={handleToggleStar}
            className={`p-2 ${game.is_starred ? 'text-yellow-400' : 'text-gray-400'} hover:text-yellow-400`}
          >
            <Star className={`w-6 h-6 ${game.is_starred ? 'fill-yellow-400' : ''}`} />
          </button>
          <button
            data-testid="delete-game-btn"
            onClick={() => setShowDeleteModal(true)}
            className="p-2 text-gray-400 hover:text-red-500"
          >
            <Trash2 className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="px-4">
        {/* Edit Time Remaining Badge */}
        {canEdit.can_edit && (
          <div className="flex justify-center mb-4">
            <span className="flex items-center gap-1 text-xs bg-[#00E5E5]/20 text-[#00E5E5] px-3 py-1 rounded-full">
              <Clock className="w-3 h-3" />
              {canEdit.hours_remaining}h left to edit
            </span>
          </div>
        )}

        {/* Game Header */}
        <div className="text-center mb-6">
          <span className="text-5xl block mb-2">{sportConfig?.emoji}</span>
          <h1 className="text-2xl font-bold text-white mb-1">{sportConfig?.name} Game</h1>
          <p className="text-gray-400">{format(new Date(game.date_played), 'MMMM d, yyyy')}</p>
        </div>

        {/* Primary Stat */}
        <div className="bg-[#1A1A1A] rounded-2xl p-6 mb-4">
          <div className="text-center">
            <p className="text-7xl font-black text-[#00E5E5]">{game.points}</p>
            <p className="text-white font-bold tracking-widest mt-2">{primaryLabel}</p>
          </div>
        </div>

        {/* All Stats */}
        <div className="bg-[#1A1A1A] rounded-2xl p-4 mb-4">
          <h2 className="text-sm font-semibold text-gray-400 mb-4 tracking-wider">GAME STATS</h2>
          <div className="grid grid-cols-3 gap-4">
            {sportConfig?.stats
              .filter(stat => (game.stats[stat.key] || 0) > 0)
              .map(stat => (
                <div key={stat.key} className="text-center">
                  <p className="text-2xl font-bold" style={{ color: stat.color }}>
                    {game.stats[stat.key]}
                  </p>
                  <p className="text-xs text-gray-400">{stat.shortLabel}</p>
                </div>
              ))}
          </div>
          {Object.values(game.stats).every(v => v === 0) && (
            <p className="text-center text-gray-500">No stats recorded</p>
          )}
        </div>

        {/* Notes */}
        {game.notes && (
          <div className="bg-[#1A1A1A] rounded-2xl p-4">
            <h2 className="text-sm font-semibold text-gray-400 mb-2 tracking-wider">NOTES</h2>
            <p className="text-white">{game.notes}</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-sm">
            <h2 className="text-xl font-bold text-white mb-2">Delete Game?</h2>
            <p className="text-gray-400 mb-6">
              This will permanently delete this game. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-[#333] text-white font-semibold py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete-game-btn"
                onClick={handleDeleteGame}
                className="flex-1 bg-red-500 text-white font-bold py-3 rounded-xl"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Game Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md my-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Edit Game</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Time remaining warning */}
            <div className="flex items-center gap-2 text-xs bg-[#FF9500]/20 text-[#FF9500] px-3 py-2 rounded-lg mb-4">
              <Clock className="w-4 h-4" />
              <span>{canEdit.hours_remaining}h remaining to edit this game</span>
            </div>

            {/* Batting/Pitching Toggle for baseball/softball */}
            {showBattingPitching && (
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setEditMode('batting')}
                  className={`flex-1 py-2 rounded-lg font-semibold ${
                    editMode === 'batting' ? 'bg-[#00E5E5] text-black' : 'bg-[#333] text-white'
                  }`}
                >
                  Batting
                </button>
                <button
                  onClick={() => setEditMode('pitching')}
                  className={`flex-1 py-2 rounded-lg font-semibold ${
                    editMode === 'pitching' ? 'bg-[#00E5E5] text-black' : 'bg-[#333] text-white'
                  }`}
                >
                  Pitching
                </button>
              </div>
            )}

            {/* Stats Grid */}
            <div className="space-y-3 max-h-[40vh] overflow-y-auto mb-4">
              {getEditableStats().map(stat => (
                <div key={stat.key} className="flex items-center justify-between bg-[#000] rounded-xl p-3">
                  <div>
                    <p className="text-white font-semibold">{stat.label}</p>
                    <p className="text-xs text-gray-500">{stat.shortLabel}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleStatChange(stat.key, -1)}
                      className="w-11 h-11 rounded-full bg-[#333] text-white font-bold text-xl flex items-center justify-center hover:bg-[#444]"
                    >
                      -
                    </button>
                    <span className="text-2xl font-bold text-white w-12 text-center">
                      {editStats[stat.key] || 0}
                    </span>
                    <button
                      onClick={() => handleStatChange(stat.key, 1)}
                      className="w-11 h-11 rounded-full bg-[#00E5E5] text-black font-bold text-xl flex items-center justify-center hover:bg-[#5FFBFB]"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Notes */}
            <div className="mb-4">
              <label className="text-sm text-gray-400 block mb-1">Notes</label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Add notes about this game..."
                className="w-full bg-[#000] text-white rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5] h-20 resize-none"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 bg-[#333] text-white font-semibold py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                data-testid="save-edit-btn"
                onClick={handleSaveEdit}
                disabled={isSaving}
                className="flex-1 bg-[#00E5E5] text-black font-bold py-3 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving ? 'Saving...' : (
                  <>
                    <Check className="w-5 h-5" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Game Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md my-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Share Game</h2>
              <button onClick={() => setShowShareModal(false)} className="text-gray-400">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Preview Card - Sleek Design */}
            <div 
              ref={shareCardRef}
              className="rounded-2xl overflow-hidden mb-4"
              style={{ minHeight: '400px' }}
            >
              {/* Cyan Header */}
              <div className="bg-[#00E5E5] py-4 px-6 text-center">
                <div className="flex justify-center gap-1 text-2xl mb-2">
                  <span>🏀</span>
                  <span>🏈</span>
                  <span>⚾</span>
                  <span>⚽</span>
                  <span>🏐</span>
                  <span>🥎</span>
                </div>
                <h1 className="text-2xl font-black text-black tracking-wide">IN THE STANDS</h1>
                <p className="text-black/80 font-semibold text-sm tracking-widest mt-1">
                  {sportConfig?.emoji} {sportConfig?.name?.toUpperCase()} REPORT
                </p>
              </div>

              {/* Black Content Section */}
              <div className="bg-[#000] px-6 py-5">
                {/* Athlete Name & Date */}
                <div className="text-center border-b border-gray-700 pb-4 mb-4">
                  <h2 className="text-2xl font-black text-white tracking-wide uppercase">{athlete?.name}</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    {format(new Date(game.date_played), 'EEE, MMM d, yyyy')}
                  </p>
                </div>

                {/* Main Score */}
                <div className="text-center py-6">
                  <p className="text-8xl font-black text-[#00E5E5]">{game.points}</p>
                  <p className="text-white font-bold tracking-[0.3em] text-lg mt-2">{primaryLabel}</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {sportConfig?.stats
                    .filter(stat => (game.stats[stat.key] || 0) > 0)
                    .slice(0, 6)
                    .map(stat => (
                      <div key={stat.key} className="bg-[#1A1A1A] rounded-xl p-4 text-center">
                        <p className="text-3xl font-black" style={{ color: stat.color }}>
                          {game.stats[stat.key]}
                        </p>
                        <p className="text-xs font-semibold tracking-wider mt-1" style={{ color: stat.color }}>
                          {stat.shortLabel}
                        </p>
                      </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="text-center pt-3 border-t border-gray-800">
                  <p className="text-gray-500 text-sm">
                    Generated by <span className="text-[#00E5E5] font-bold">IN THE STANDS</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleDownload}
                disabled={isGeneratingImage}
                className="flex-1 bg-[#333] text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Save Image
              </button>
              <button
                onClick={handleShare}
                disabled={isGeneratingImage}
                className="flex-1 bg-[#00E5E5] text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2"
              >
                {isGeneratingImage ? 'Generating...' : (
                  <>
                    <Share2 className="w-5 h-5" />
                    Share
                  </>
                )}
              </button>
            </div>

            <p className="text-center text-xs text-gray-500 mt-3">
              Tap Share to send via text, or Save Image to share later
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
